import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, ButtonGroup, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH, PENSION_PAYOUT_INTEREST_RATE } from "Components/Analyses/constants";
import { Button as AppButton } from "Components/_Shared/Buttons/Button";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as IconBarChart } from "Components/_Shared/Icons/Assets/bar-chart.svg";
import { ReactComponent as IconLineChart } from "Components/_Shared/Icons/Assets/line-chart.svg";
import { ReactComponent as ResetIcon } from "Components/_Shared/Icons/Assets/reset.svg";
import { StyledHtmlLink } from "Components/_Shared/Routing/UnstyledLink";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import {
  type PensionGoalFormContext,
  type PensionGoalFormType,
  getPensionGoalFormSchema,
  minDate,
  minRetirementDate,
} from "Forms/Schemas/Goals/pensionGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetPensionGoalQueryKey,
  useCreatePensionGoal,
  useGetAnalysisClients,
  useUpdatePensionGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemRelationType, type PensionGoalDto, PensionGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  DEFAULT_PENSION_DURATION,
  INTEREST_RATE_AGGRESIVE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { format, isBefore, parseISO, startOfDay } from "date-fns";
import { forwardRef, useContext, useId, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePensionCalculations } from "../Hooks/usePensionCalculations";
import { ModelingAccordion } from "./ModelingAccordion";
import { ModelingHint } from "./ModelingHint";
import { PensionChart } from "./PensionChart";
import { PensionDifferenceChart } from "./PensionDifferenceChart";
import { PensionGoalModelingHint } from "./PensionGoalModelingHint";
import { buildPensionGoalData, buildPensionGoalModeling } from "./pensionGoalPayload";
import { ageMonthsToYears, retirementStartAgeToMonths } from "./pensionRetirementDate";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: PensionGoalDto;
  readonly goalId?: string;
};

export const PensionGoalForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const [chartType, setChartType] = useState<"line" | "bar">("line");
  const retirementDateHintId = useId();

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);
  const birthDate = clientsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client)?.birthDate;
  const clientBirthDate = useMemo(() => (birthDate ? parseISO(birthDate) : null), [birthDate]);
  const isClientBirthDateMissing = !!clientsData && !clientBirthDate;

  const retirementStartAgeMonths = retirementStartAgeToMonths(editGoal?.data.retirementStartAge);

  const { mutate: createPensionGoal } = useCreatePensionGoal();

  const { mutate: updatePensionGoal } = useUpdatePensionGoal();

  const methods = useForm<PensionGoalFormType>({
    resolver: yupResolver(getPensionGoalFormSchema(t)),
    context: { hasClientBirthDate: !!clientBirthDate } satisfies PensionGoalFormContext,
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          infinitePension: editGoal.data.isInfinityRentRequired ?? false,
          includeStatePension: editGoal.data.isRetirementPensionExpected ?? false,
          includeInflation: editGoal.modeling.inflation?.enabled,
          includeFee: editGoal.modeling.fee?.enabled,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          inflationRate: (editGoal.modeling.inflation?.rate?.toString() ?? "0") as any,
          feeRate: (editGoal.modeling.fee?.rate?.toString() ?? "0") as any,
          privatePensionDuration: editGoal.data.retirementPayoutInterval?.value.toString() as any,
          pensionInterestRate: editGoal.modeling.payoutAppreciationRate?.toString() as any,
          privatePensionStartAge:
            retirementStartAgeMonths === undefined ? undefined : (ageMonthsToYears(retirementStartAgeMonths).toString() as any),
          privatePensionStartAgeMonths: retirementStartAgeMonths,
          privatePensionStartDate: null,
          statePensionAmount: editGoal.data.expectedRetirementAmount?.value.toString() as any,
          privatePensionAmount: editGoal.data.rentAmount?.value.toString() as any,
          // `new Date(null)` is the epoch, not an empty date — casting the null away here used to silently seed the
          // field with 1.1.1970, which then tripped the "draw start date is in the past" guard forever. Keep it
          // null and let the server's derived default fill it in.
          statePensionStart: editGoal.data.retirementStartDate ? parseISO(editGoal.data.retirementStartDate) : null,
          monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value.toString() as any,
          targetAmount: editGoal.modeling.targetAmount?.value.toString() as any,
          isTargetAmountManual: editGoal.modeling.isTargetAmountManual ?? false,
          isInvestmentAmountManual: editGoal.modeling.isInvestmentAmountManual ?? false,
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === PensionGoalDtoPriorityLevel.high,
          netIncomeAmount: editGoal.data.netSalaryAmount?.value.toString() as any,
          grossIncomeAmount: editGoal.data.grossSalaryAmount?.value.toString() as any,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          name: t(Resources.AnalysisDetail.Goals.GoalType.Pension),
          infinitePension: false,
          includeStatePension: false,
          includeInflation: true,
          includeFee: true,
          interestRate: INTEREST_RATE_AGGRESIVE.toString() as any,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          feeRate: DEFAULT_FEE_RATE.toString() as any,
          privatePensionDuration: DEFAULT_PENSION_DURATION.toString() as any,
          pensionInterestRate: PENSION_PAYOUT_INTEREST_RATE.toString() as any,
          referenceStartDate: new Date(),
          privatePensionStartDate: null,
          statePensionStart: null,
          isTargetAmountManual: false,
          isInvestmentAmountManual: false,
        },
  });

  const {
    control,
    setValue,
    watch,
    getValues,
    resetField,
    formState: { errors, dirtyFields },
    handleSubmit,
    clearErrors,
    trigger,
  } = methods;

  // data
  const infinitePension = watch("infinitePension");
  const includeStatePension = watch("includeStatePension");
  const netIncomeAmount = watch("netIncomeAmount");
  const statePensionAmount = watch("statePensionAmount");
  const statePensionStart = watch("statePensionStart");
  const privatePensionAmount = watch("privatePensionAmount");
  const privatePensionDuration = watch("privatePensionDuration");

  // modeling
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const targetAmount = watch("targetAmount");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const pensionInterestRate = watch("pensionInterestRate");

  const referenceStartDate = watch("referenceStartDate");
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const isInvalidDrawStartDate = statePensionStart && isBefore(startOfDay(statePensionStart), startOfDay(minDate));

  const {
    preview,
    calculations: { clientAge, yearsRemaining, isWithdrawBeforeStatePensionRequired },
    handlers: {
      handleInfinitePensionClick,
      handleIncludeStatePensionClick,
      handleRetirementAgeChange,
      handleRetirementAgeBlur,
      handleRetirementDateChange,
      handleRetirementDateBlur,
      handlePrivatePensionDurationBlur,
      handleNetIncomeAmountBlur,
      handlePrivatePensionAmountBlur,
      handleStatePensionAmountBlur,
      handleInflationBlur,
      handleWithdrawInterestRateBlur,
      handleIncludeInflationClick,
      handleIncludeFeeChange,
      handleFeeRateBlur,
      handleInterestRateBlur,
      // handleTargetAmountBlur,
      // handleMonthlyContributionWithFeeBlur,
      handleResetToDefaults,
    },
  } = usePensionCalculations({
    analysisId,
    versionId,
    goalGuid: editGoal?.goalId,
    watch,
    setValue,
    getValues,
    resetField,
    clearErrors,
    trigger,
    dirtyFields,
    isEditMode: !!editGoal,
    isInvalidDrawStartDate: !!isInvalidDrawStartDate,
    clientBirthDate,
  });

  const onCreateGoal = (data: PensionGoalFormType) => {
    createPensionGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? PensionGoalDtoPriorityLevel.high : PensionGoalDtoPriorityLevel.low,
          data: buildPensionGoalData(data),
          modeling: buildPensionGoalModeling(data),
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: PensionGoalFormType) => {
    updatePensionGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? PensionGoalDtoPriorityLevel.high : PensionGoalDtoPriorityLevel.low,
          data: buildPensionGoalData(data),
          modeling: buildPensionGoalModeling(data),
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetPensionGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  // "Do we have enough input to model this?" used to be answered here by testing the individual fields. The server
  // answers it now: it nulls out what it can't compute yet, so anything else is second-guessing it — and the field
  // tests got it wrong anyway, since they were truthiness checks on strings where a legitimate "0" reads as false.
  const shouldRenderModelingAndChart = () => {
    if (isInvalidDrawStartDate) return false;

    return !!preview && preview.targetAmount !== null && preview.retirementMonth !== null;
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Pension)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  disabled={readOnly}
                  control={control}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={12}>
                <FormSwitch
                  disabled={readOnly}
                  control={control}
                  name="infinitePension"
                  label={t(Resources.AnalysisDetail.Goals.InfinitePension)}
                  errors={errors}
                  onClick={() => {
                    const newInfinitePensionValue = !infinitePension;
                    handleInfinitePensionClick(newInfinitePensionValue);
                  }}
                />
              </Grid>
              <Grid size={12}>
                <FormSwitch
                  control={control}
                  disabled={readOnly}
                  name="includeStatePension"
                  label={t(Resources.AnalysisDetail.Goals.IncludeStatePension)}
                  errors={errors}
                  onClick={() => {
                    const newIncludeStatePensionValue = !includeStatePension;
                    handleIncludeStatePensionClick(newIncludeStatePensionValue);
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="privatePensionStartAge"
                      label={t(Resources.AnalysisDetail.Goals.RetirementAge)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          scale: 0,
                          min: clientAge,
                          max: 100,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.Years)}
                        </Typography>
                      }
                      // TextField points aria-describedby at its own error text; overriding it must keep that link.
                      slotProps={{ htmlInput: { "aria-describedby": `${retirementDateHintId} privatePensionStartAge-helper-text` } }}
                      onChange={handleRetirementAgeChange}
                      onBlur={handleRetirementAgeBlur}
                    />
                  </Grid>
                  <Grid size={6}>
                    <DatePicker
                      control={control}
                      name="privatePensionStartDate"
                      label={t(Resources.AnalysisDetail.Goals.RetirementDate)}
                      labelEndAdornment={clientBirthDate ? <StyledLabelAdornment /> : undefined}
                      views={["year", "month"]}
                      minDate={minRetirementDate}
                      errors={errors}
                      disabled={readOnly || !clientBirthDate}
                      ariaDescribedBy={retirementDateHintId}
                      onChange={handleRetirementDateChange}
                      onBlur={handleRetirementDateBlur}
                    />
                  </Grid>
                </Grid>
                {(clientBirthDate || isClientBirthDateMissing) && (
                  <Typography id={retirementDateHintId} variant="body2" fontSize={13} color="grey.700" mt={Spacing.S}>
                    {clientBirthDate
                      ? t(Resources.AnalysisDetail.Goals.RetirementAgeDateHint, { birthDate: format(clientBirthDate, "MM/yyyy") })
                      : t(Resources.AnalysisDetail.Goals.RetirementDateMissingBirthDate)}
                  </Typography>
                )}
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  disabled={readOnly || infinitePension}
                  control={control}
                  name="privatePensionDuration"
                  label={t(Resources.AnalysisDetail.Goals.PensionDuration)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  mask={[
                    {
                      mask: Number,
                      scale: 0,
                      min: 1,
                      max: 100,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Years)}
                    </Typography>
                  }
                  onBlur={handlePrivatePensionDurationBlur}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="privatePensionAmount"
                  label={t(Resources.AnalysisDetail.Goals.MonthlyPensionAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  onBlur={handlePrivatePensionAmountBlur}
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                      min: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      Kč
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
          {includeStatePension && (
            <Box component="section" p={Spacing.XL} borderRadius={Radius.L} sx={(theme) => ({ bgcolor: theme.colors.backgroundSecondary })}>
              <HStack justifyContent="space-between" alignItems="center" mb={Spacing.XL}>
                <Typography component="h3" variant="body1" fontSize={17} fontWeight={700}>
                  {t(Resources.AnalysisDetail.Goals.StatePensionSectionTitle)}
                </Typography>
                <AppButton disabled={readOnly} color="secondary" endAdornment={<ResetIcon />} onClick={handleResetToDefaults}>
                  {t(Resources.AnalysisDetail.Goals.ResetToDefaultValues)}
                </AppButton>
              </HStack>
              <Grid container spacing={Spacing.XL}>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="netIncomeAmount"
                    label={t(Resources.AnalysisDetail.Goals.NetIncome)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    onBlur={handleNetIncomeAmountBlur}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="statePensionAmount"
                    label={t(Resources.AnalysisDetail.Goals.StatePensionAmount)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    onBlur={handleStatePensionAmountBlur}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <DatePicker
                    control={control}
                    name="statePensionStart"
                    label={t(Resources.AnalysisDetail.Goals.StatePensionStart)}
                    errors={errors}
                    disabled
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" fontSize={13} color="grey.700" mt={Spacing.M}>
                {t(Resources.AnalysisDetail.Goals.StatePensionDerivedFromNetIncome)}{" "}
                {t(Resources.AnalysisDetail.Goals.DetailsCanBeFoundOnSite)}{" "}
                <StyledHtmlLink target="_blank" rel="noopener" href="https://www.cssz.cz/">
                  {t(Resources.AnalysisDetail.Goals.Cssz)}
                </StyledHtmlLink>
                .
              </Typography>
            </Box>
          )}
          <Box>
            {!!preview && !!statePensionStart && shouldRenderModelingAndChart() ? (
              <Box mb={Spacing.XL}>
                <PensionGoalModelingHint
                  feeRate={Number(feeRate)}
                  inflationRate={Number(inflationRate)}
                  interestRate={Number(interestRate)}
                  pensionInterestRate={Number(pensionInterestRate)}
                  targetAmount={Number(targetAmount)}
                  monthlyContribution={Number(monthlyContributionWithFee)}
                  yearsRemaining={yearsRemaining}
                  pensionAmount={Number(privatePensionAmount)}
                  pensionDuration={Number(privatePensionDuration)}
                  infinitePension={infinitePension}
                />
              </Box>
            ) : null}
            {shouldRenderModelingAndChart() ? (
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <BlFormInput
                      disabled
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 2,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          Kč
                        </Typography>
                      }
                      // onBlur={() => handleMonthlyContributionWithFeeBlur(Number(monthlyContributionWithFee))}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      disabled
                      control={control}
                      name="targetAmount"
                      label={t(Resources.AnalysisDetail.Goals.TargetAmount)}
                      errors={errors}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          Kč
                        </Typography>
                      }
                      // onBlur={handleTargetAmountBlur}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.PensionAccumulationInterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onBlur={handleInterestRateBlur}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          min: 0,
                          max: 20,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="pensionInterestRate"
                      label={t(Resources.AnalysisDetail.Goals.DrawdownInterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onBlur={handleWithdrawInterestRateBlur}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          min: 0,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      disabled={readOnly}
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      errors={errors}
                      onChange={handleIncludeInflationClick}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          scale: 1,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                        },
                      ]}
                      onBlur={handleInflationBlur}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      onChange={handleIncludeFeeChange}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      onBlur={handleFeeRateBlur}
                      mask={[
                        {
                          mask: Number,
                          scale: 1,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    {preview?.phases && statePensionAmount && includeStatePension && isWithdrawBeforeStatePensionRequired && (
                      <ModelingHint title={t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.Title)}>
                        {t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.FirstPhrase)}{" "}
                        <Typography display="inline" variant="body2" fontWeight={500}>
                          do {format(parseISO(preview.phases.statePensionStartDate), "dd.MM.yyyy")}
                        </Typography>
                        , {t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.SecondPhrase)}{" "}
                        <Typography display="inline" variant="body2" fontWeight={500}>
                          {formatCurrency(preview.phases.monthlyWithdrawal, CurrencyCode.CZK)}
                        </Typography>
                        .
                      </ModelingHint>
                    )}
                  </Grid>
                </Grid>
              </ModelingAccordion>
            ) : null}
          </Box>
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderModelingAndChart() && (
                <Grid size={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <ButtonGroup sx={{ mb: theme.spacing(2) }}>
                      <Button
                        variant="outlined"
                        onClick={() => setChartType("line")}
                        sx={{
                          height: "40px",
                          borderRadius: theme.spacing(Radius.M),
                          borderColor: theme.colors.borderLight,
                          bgcolor: chartType === "line" ? theme.colors.backgroundSecondary : "transparent",
                          color: chartType === "line" ? theme.colors.textPrimary : theme.colors.textSecondary,
                          boxShadow: "none",
                        }}
                      >
                        <IconLineChart />
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setChartType("bar")}
                        sx={{
                          height: "40px",
                          borderRadius: theme.spacing(Radius.M),
                          borderColor: theme.colors.borderLight,
                          bgcolor: chartType === "bar" ? theme.colors.backgroundSecondary : "transparent",
                          color: chartType === "bar" ? theme.colors.textPrimary : theme.colors.textSecondary,
                          boxShadow: "none",
                        }}
                      >
                        <IconBarChart />
                      </Button>
                    </ButtonGroup>
                  </Box>
                  {chartType === "bar" && (
                    <PensionDifferenceChart netIncomeAmount={Number(netIncomeAmount)} statePensionAmount={Number(statePensionAmount)} />
                  )}
                  {chartType === "line" && (
                    <>
                      <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
                        <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                          <Typography variant="caption" fontWeight={500}>
                            {t(Resources.Common.Investment)}
                          </Typography>
                          <Typography variant="caption">
                            {formatCurrency(monthlyContributionWithFee, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                          </Typography>
                        </Stack>
                        <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                          <Typography variant="caption" fontWeight={500}>
                            {t(Resources.AnalysisDetail.Goals.DrawProcess)}
                          </Typography>
                          <Typography variant="caption">
                            {formatCurrency(privatePensionAmount, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                          </Typography>
                        </Stack>
                      </Box>
                      {preview && <PensionChart preview={preview} referenceStartDate={referenceStartDate} />}
                    </>
                  )}
                </Grid>
              )}
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
